import axios from "axios";
import buildUrl from "build-url-ts";
import {
  PagedResponse,
  ReportRequest,
  ReportSummaryResponse,
} from "src/types/experiences/leadsAndReports";

const BASE_URL = process.env.REACT_APP_CLEVERSTORY_API_URL;
const PAPERFLITE_API_URL = process.env.REACT_APP_API_URL;

const STORYBOARD_REPORTS = `${BASE_URL}/api/1.0/storyboard/reports`;
const INTERACTIVE_REPORTS = `${BASE_URL}/api/1.0/interactive/reports`;
const LANDING_REPORTS = `${BASE_URL}/api/1.0/landing_page/reports`;
const ASSETS = `${PAPERFLITE_API_URL}/api/2.0/assets`;

const post = async <T = any>(url: string | undefined, body?: unknown): Promise<T> => {
  const { data } = await axios.post(url ?? "", body);
  return data;
};

export const reportsApi = {
  // R1 — report summary tiles
  fetchSummary: (request: ReportRequest): Promise<ReportSummaryResponse> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/summary` }), request),

  // R2 — sessions (visitors)
  fetchSessions: (request: ReportRequest, page: number, limit: number): Promise<PagedResponse> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/sessions?page=${page}&limit=${limit}` }), request),

  // R3 — sessions time series
  fetchSessionsTimeSeries: (request: ReportRequest): Promise<any> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/sessions/graph` }), request),

  // R5 — interactions
  fetchInteractions: (request: ReportRequest, page: number, limit: number): Promise<PagedResponse> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/interactions?page=${page}&limit=${limit}` }), request),

  // R6 — leads time series
  fetchLeadsTimeSeries: (request: ReportRequest): Promise<any> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/leads/graph` }), request),

  // R7 — metrics by type (device/organization/utm_*/country/leads)
  fetchMetrics: (request: ReportRequest, page: number, limit: number): Promise<PagedResponse> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/metrics/${request.metricType}?page=${page}&limit=${limit}` }), request),

  fetchLeadRecord: async (
    request: ReportRequest,
    profileId: string,
  ): Promise<any | null> => {
    const limit = 50;
    for (let page = 0; page < 40; page += 1) {
      const res = await reportsApi.fetchMetrics(
        { ...request, metricType: "leads" },
        page,
        limit,
      );
      const records = res?.records ?? [];
      const match = records.find((r: any) => r?.contact?.id === profileId);
      if (match) return match;
      const totalPages = res?.totalPages ?? 1;
      if (records.length === 0 || page >= totalPages - 1) break;
    }
    return null;
  },

  // R9 — asset analytics
  fetchAssetAnalytics: (request: ReportRequest, page: number, limit: number): Promise<PagedResponse> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/assets?page=${page}&limit=${limit}` }), request),

  // R10 — per-asset trend chart
  fetchAssetTrend: (request: { assetMappingId: string; contact?: string } & Record<string, unknown>): Promise<any> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/asset/analytics` }), request),

  // R11 — document preview (GET)
  fetchDocumentPreview: async (assetId: string): Promise<any> => {
    const url = buildUrl(ASSETS, { path: `/${assetId}/preview` });
    const { data } = await axios.get(url ?? "");
    return data;
  },

  fetchJourney: (request: ReportRequest, page: number, limit: number): Promise<PagedResponse> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/journey?page=${page}&limit=${limit}` }), request),

  fetchPageAnalytics: (
    request: ReportRequest,
    variant: "interactive" | "landing",
    page: number,
    limit: number,
  ): Promise<PagedResponse> =>
    post(
      buildUrl(variant === "interactive" ? INTERACTIVE_REPORTS : LANDING_REPORTS, {
        path: `/pages?page=${page}&limit=${limit}`,
      }),
      request,
    ),

  fetchRowTrend: async (
    request: Record<string, unknown>,
    variant: "interactive" | "landing",
    pages: any[] = [],
  ): Promise<any[]> => {
    const response = await post<any>(
      buildUrl(variant === "interactive" ? INTERACTIVE_REPORTS : LANDING_REPORTS, { path: `/analytics` }),
      request,
    );
    const analytics: any[] = response?.pageAnalytics ?? [];

    if (variant === "landing" && pages.length) {
      const order = pages.map((p) => p.id);
      const sorted = [...analytics].sort(
        (a, b) => order.indexOf(a.id) - order.indexOf(b.id),
      );
      return sorted.map((item, index) => ({
        ...item,
        name: pages[index]?.name ?? item.name,
      }));
    }
    return analytics;
  },

  fetchSessionsForDownload: (request: ReportRequest, page: number, limit: number): Promise<PagedResponse> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/sessions?page=${page}&limit=${limit}` }), request),

  fetchAssetsForDownload: (request: ReportRequest, page: number, limit: number): Promise<PagedResponse> =>
    post(buildUrl(STORYBOARD_REPORTS, { path: `/assets?page=${page}&limit=${limit}` }), request),

  // R21 — all interactive pages (no body)
  fetchAllInteractivePages: (): Promise<any> =>
    post(buildUrl(INTERACTIVE_REPORTS, { path: `/pages` })),

  // R22 — all landing_page pages
  fetchAllLandingPages: (storyboardId: string): Promise<any> =>
    post(buildUrl(`${BASE_URL}/api/1.0/pages`, { path: `/search` }), { storyboard: storyboardId }),
};

/** Max pages fetched concurrently, to keep the download fast without flooding the API. */
const DOWNLOAD_CONCURRENCY = 6;

export const downloadAllPages = async (
  // eslint-disable-next-line no-unused-vars
  fetchPage: (page: number, limit: number) => Promise<PagedResponse>,
  limit = 50,
): Promise<any[]> => {
  const first = await fetchPage(0, limit);
  const totalPages = Math.min(first.totalPages ?? 1, 1000);
  const all: any[] = [...(first.records ?? [])];
  if (totalPages <= 1) return all;

  // Remaining page indices: 1 .. totalPages - 1.
  const pages = Array.from({ length: totalPages - 1 }, (_, i) => i + 1);
  for (let i = 0; i < pages.length; i += DOWNLOAD_CONCURRENCY) {
    const batch = pages.slice(i, i + DOWNLOAD_CONCURRENCY);
    const results = await Promise.all(batch.map((page) => fetchPage(page, limit)));
    for (const res of results) all.push(...(res.records ?? []));
  }
  return all;
};
